import React, { useCallback, useEffect, useReducer } from 'react';
import { useRecoilState } from 'recoil';
import JustificationContainer from '~/components/atom/JustificationContainer';
import { Heading5 } from '~/components/atom/Typography';
import RadioButton from '~/components/molecule/RadioButton';
import Textarea from '~/components/molecule/Textarea';
import DateTimePicker from '~/components/organism/DateTimePicker';
import servicesFeedbackState from '~/components/page/LFA/state/servicesFeedbackState';
import type {
  Date__Input,
  Lfa_Conversion_Data_Rejection_Criteria,
} from '~/graphql/types';
import { formatToYYYYMMDD } from '~/util/date';
import useErrorReporter from '~/hooks/useErrorReporter';
import useAddToast from '~/hooks/useAddToast';
import formatToastMessage from '~/util/formatToastMessage';
import RejectBlock from '../RejectBlock';
import reducer, { type State } from './utils/reducer';
import ErrorBlock from '../ErrorBlock';
import useFeedbackValidation from '~/components/page/LFA/hooks/useFeedbackValidation';
import hasValue from '~/util/hasValue';

const text = {
  question: 'Waarom is de afspraak niet doorgegaan?',
  rescheduled: 'Afspraak is verplaatst',
  rejected: 'Afspraak is niet doorgegaan om een andere reden',
};

type Props = {
  conversionId: string;
  rejectionReasons: Array<Lfa_Conversion_Data_Rejection_Criteria>;
};

const initialState: State = {
  reasonType: 'rescheduled',
  rejectionReasons: [],
  rescheduledDate: null,
  optionalNote: '',
  customReasonText: '',
};

const AppointmentNotHappened: React.FC<Props> = ({ rejectionReasons }) => {
  const errorReporter = useErrorReporter();
  const addToast = useAddToast();
  const [, setConversionFeedback] = useRecoilState(servicesFeedbackState);
  const {
    clearValidationErrorForField,
    clearValidationErrors,
    errorsToShowLookup,
  } = useFeedbackValidation({
    validationFor: ['Failed', 'Rescheduled'],
  });

  const [state, dispatch] = useReducer(reducer, initialState);

  const handleReasonTypeChange = useCallback(
    (reasonType: 'rescheduled' | 'rejected') => {
      clearValidationErrors();
      dispatch({ type: 'SET_REASON_TYPE', payload: { reasonType } });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const handleRescheduleDateChange = useCallback(
    (date: string) => {
      try {
        clearValidationErrorForField({
          key: 'Rescheduled',
          field: 'rescheduledDate',
        });
        const formattedDate = formatToYYYYMMDD(date);

        if (formattedDate) {
          const definedTime = state.rescheduledDate?.DayTime?.t;
          const newDate = definedTime
            ? { DayTime: { d: formattedDate, t: definedTime } }
            : { Day: { d: formattedDate } };

          dispatch({
            type: 'SET_RESCHEDULED_DATE',
            payload: { date: newDate },
          });
        }
      } catch (error) {
        errorReporter.captureException(error as Error, 'error');
        addToast([
          formatToastMessage('Datum kon niet worden ingesteld', 'danger'),
        ]);
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [state.rescheduledDate, errorReporter, addToast],
  );

  const handleTimeChange = useCallback(
    (time: string) => {
      try {
        const newDate = hasValue(time)
          ? {
              DayTime: {
                d:
                  state.rescheduledDate?.Day?.d ||
                  state.rescheduledDate?.DayTime?.d ||
                  '',
                t: hasValue(time) ? `${time}:00` : '',
              },
            }
          : {
              Day: {
                d:
                  state.rescheduledDate?.Day?.d ||
                  state.rescheduledDate?.DayTime?.d ||
                  '',
              },
            };
        dispatch({ type: 'SET_RESCHEDULED_DATE', payload: { date: newDate } });
      } catch (error) {
        errorReporter.captureException(error, 'error');
        addToast([
          formatToastMessage('Tijd kon niet worden ingesteld', 'danger'),
        ]);
      }
    },
    [state.rescheduledDate, errorReporter, addToast],
  );

  const handleOptionalNoteChange = useCallback((note: string) => {
    dispatch({ type: 'SET_OPTIONAL_NOTE', payload: { note } });
  }, []);

  const handleCustomReasonTextChange = useCallback((text: string) => {
    clearValidationErrorForField({
      key: 'Failed',
      field: 'customReason',
    });
    dispatch({ type: 'SET_CUSTOM_REASON_TEXT', payload: { text } });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleToggleRejectionReason = useCallback(
    (reason: Lfa_Conversion_Data_Rejection_Criteria) => {
      clearValidationErrorForField({
        key: 'Failed',
        field: 'criteria',
      });
      clearValidationErrorForField({
        key: 'Failed',
        field: 'customReason',
      });
      dispatch({ type: 'TOGGLE_REJECTION_REASON', payload: { reason } });
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  );

  const buildServicesFeedback = useCallback(() => {
    try {
      if (state.reasonType === 'rescheduled') {
        return {
          Event_Feedback_Appointment_Rescheduled: {
            // Even though at this point `rescheduledDate` can be null, we allow it, because the validation will catch it
            appointmentDate: state.rescheduledDate as Date__Input,
            ...(state.optionalNote ? { note: state.optionalNote } : {}),
          },
        };
      } else {
        return {
          Event_Feedback_Appointment_Failed: {
            rejectionCriteria:
              state.rejectionReasons.length > 0
                ? state.rejectionReasons.map(reason => ({
                    id: reason.id,
                    reason:
                      reason.id === 'custom_reason'
                        ? state.customReasonText
                        : reason.reason || '',
                  }))
                : [],
          },
        };
      }
    } catch (error) {
      errorReporter.captureException(error as Error, 'error');
      return addToast([
        formatToastMessage('Feedback kon niet worden bijgewerkt', 'danger'),
      ]);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  useEffect(() => {
    const feedback = buildServicesFeedback();
    if (feedback) {
      setConversionFeedback(feedback);
    }
  }, [buildServicesFeedback, setConversionFeedback]);

  const rescheduledDateError = errorsToShowLookup['rescheduledDate']?.[0];
  return (
    <JustificationContainer direction="column" width="100%">
      <Heading5 required>{text.question}</Heading5>
      <JustificationContainer gap="xs" direction="column" width="100%">
        <RadioButton
          label={text.rescheduled}
          checked={state.reasonType === 'rescheduled'}
          onChange={() => handleReasonTypeChange('rescheduled')}
        />
        {state.reasonType === 'rescheduled' && (
          <JustificationContainer
            direction="column"
            border={{
              radius: 'base',
              color: { group: rescheduledDateError ? 'danger' : 'tertiary' },
              width: 's',
            }}
            padding={['base']}
            gap="s"
            backgroundColor={{
              group: rescheduledDateError ? 'danger' : 'tertiary',
              variant: 'translucent',
            }}
            width="100%"
          >
            {rescheduledDateError && (
              <ErrorBlock error={rescheduledDateError} />
            )}
            <JustificationContainer align="end" width="100%">
              <DateTimePicker
                dateProps={{
                  date: state.rescheduledDate?.Day?.d || '',
                  onDateChange: handleRescheduleDateChange,
                }}
                timeProps={{
                  time: state.rescheduledDate?.DayTime?.t,
                  onTimeChange: handleTimeChange,
                }}
              />
            </JustificationContainer>
            <Textarea
              label="Aanvullende opmerkingen (optioneel)"
              name="optional-note"
              value={state.optionalNote}
              onChange={value => handleOptionalNoteChange(value || '')}
            />
          </JustificationContainer>
        )}
        <RadioButton
          label={text.rejected}
          checked={state.reasonType === 'rejected'}
          onChange={() => handleReasonTypeChange('rejected')}
        />
        {state.reasonType === 'rejected' && (
          <RejectBlock
            error={
              errorsToShowLookup['criteria']?.[0] ||
              errorsToShowLookup['customReason']?.[0]
            }
            rejectionCriteria={rejectionReasons}
            selectedCriteria={state.rejectionReasons}
            customReason={state.customReasonText}
            setCustomReason={handleCustomReasonTextChange}
            handleRejectionCriteriaChange={handleToggleRejectionReason}
          />
        )}
      </JustificationContainer>
    </JustificationContainer>
  );
};

export default AppointmentNotHappened;
